import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { ArrowLeft, Mail, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSubmitted(true);
      toast.success('Se l\'email è registrata, riceverai il link di reset.');
    } catch (error) {
      // Even on error, show generic message for security
      setSubmitted(true);
      toast.success('Se l\'email è registrata, riceverai il link di reset.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-red-50 via-white to-red-50">
        <div className="max-w-md w-full space-y-8 animate-fadeIn">
          <Card className="shadow-xl border-0 glass" data-testid="forgot-password-success-card">
            <CardHeader className="text-center pb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-white" />
              </div>
              <CardTitle className="text-2xl font-display font-bold text-gray-900">
                Controlla la tua email
              </CardTitle>
              <CardDescription className="text-gray-600 mt-2">
                Se l'email <strong>{email}</strong> è registrata nel sistema, riceverai un link per reimpostare la password.
              </CardDescription>
            </CardHeader>
            
            <CardContent className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm text-amber-800">
                  <strong>Nota:</strong> Il link scadrà tra 1 ora. Controlla anche la cartella spam.
                </p>
              </div>
              
              <div className="text-center pt-4">
                <Link 
                  to="/login" 
                  className="inline-flex items-center text-sm text-red-600 hover:text-red-700 font-medium transition-colors"
                  data-testid="back-to-login-link"
                >
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  Torna al login
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-red-50 via-white to-red-50">
      <div className="max-w-md w-full space-y-8 animate-fadeIn">
        {/* Back to login */}
        <Link 
          to="/login" 
          className="inline-flex items-center text-sm text-gray-600 hover:text-red-600 transition-colors mb-4"
          data-testid="back-to-login-link"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Torna al login
        </Link>

        <Card className="shadow-xl border-0 glass" data-testid="forgot-password-card">
          <CardHeader className="text-center pb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-red-700 rounded-full flex items-center justify-center mx-auto mb-4">
              <Mail className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-2xl font-display font-bold text-gray-900" data-testid="forgot-password-title">
              Password dimenticata?
            </CardTitle>
            <CardDescription className="text-gray-600">
              Inserisci la tua email e ti invieremo un link per reimpostare la password.
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="forgot-password-form">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium text-gray-700">
                  Email
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="custom-focus"
                  placeholder="la-tua-email@esempio.com"
                  data-testid="email-input"
                />
              </div>
              
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-semibold py-3 btn-primary"
                data-testid="submit-button"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Invio in corso...
                  </div>
                ) : (
                  'Invia link di reset'
                )}
              </Button>
            </form>
            
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600">
                Ricordi la password?{' '}
                <Link 
                  to="/login" 
                  className="font-medium text-red-600 hover:text-red-700 transition-colors"
                  data-testid="login-link"
                >
                  Accedi qui
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
